import { useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePortalLoginId } from "@hisense/shared";
import { Panel } from "@/components/ui/Panel";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Input, FieldLabel } from "@/components/ui/Input";
import {
  useOpenSupplierPortal,
  useResetSupplierPortalPassword,
  useSupplierPortal,
  useUpdateSupplierPortal,
} from "@/api/hooks";
import { getLocalizedError } from "@/lib/api";
import { FormGroup } from "@/components/ui/FormLayout";

/**
 * Cổng nhà cung cấp — mã đăng nhập, mật khẩu, khoá/mở — dùng ở trang sửa NCC và
 * ở trang Cổng (chọn NCC theo tên rồi mở/quản lý ngay).
 *
 * Anh Toàn yêu cầu 16/09/2026: trước đó phải đi ba màn hình (sinh mã ở đây,
 * tạo user ở Người dùng, dán hai UUID vào trang Cổng). Nay mọi thứ ở một chỗ.
 *
 * Panel này nằm TRONG biểu mẫu NCC bao ngoài để đứng cạnh các ô khác, nhưng
 * KHÔNG thuộc biểu mẫu đó: mã và mật khẩu gửi bằng đường API riêng, nút nào
 * cũng `type="button"`, và phím Enter bị chặn ở gốc panel — nếu không, gõ xong
 * mật khẩu rồi bấm Enter là lưu hồ sơ NCC chứ không phải mở cổng.
 *
 * Mật khẩu chỉ đi lên máy chủ; không hiển thị lại, không lưu ở đâu khác.
 */
export function SupplierPortalPanel({ supplierId, asGroup = false }: {
  supplierId: string;
  /** Trang sửa NCC: hiện như một nhóm của biểu mẫu (cùng khung các nhóm khác). Trang Cổng giữ khung cũ. */
  asGroup?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const portal = useSupplierPortal(supplierId);
  const open = useOpenSupplierPortal(supplierId);
  const update = useUpdateSupplierPortal(supplierId);
  const reset = useResetSupplierPortalPassword(supplierId);

  // `null` = chưa sửa, hiển thị mã đã lưu trên máy chủ.
  const [loginIdDraft, setLoginIdDraft] = useState<string | null>(null);
  const [pw, setPw] = useState({ password: "", confirm: "" });
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const data = portal.data;
  const account = data?.account ?? null;
  const savedLoginId = data?.loginId ?? "";
  const loginId = loginIdDraft ?? savedLoginId;
  const loginIdChanged = loginIdDraft !== null && loginIdDraft.trim() !== savedLoginId;
  const busy = open.isPending || update.isPending || reset.isPending;
  const mismatch = pw.confirm.length > 0 && pw.password !== pw.confirm;
  const pwOk = pw.password.length >= 8 && pw.password === pw.confirm;

  const run = async (action: () => Promise<unknown>, doneKey: string) => {
    setError(null);
    setDone(null);
    try {
      await action();
      setPw({ password: "", confirm: "" });
      setDone(doneKey);
    } catch (err) {
      setError(getLocalizedError(err, t, "suppliers.portalError"));
    }
  };

  const onOpen = () => run(() => open.mutateAsync({ loginId: loginId.trim(), password: pw.password }), "suppliers.portalOpened");
  const onSaveLoginId = () =>
    run(async () => {
      await update.mutateAsync({ loginId: loginId.trim() });
      setLoginIdDraft(null);
    }, "suppliers.portalLoginIdSaved");
  const onReset = () => run(() => reset.mutateAsync({ password: pw.password }), "suppliers.portalResetDone");
  const onStatus = (status: "active" | "disabled") =>
    run(() => update.mutateAsync({ status }), status === "disabled" ? "suppliers.portalDisabled" : "suppliers.portalEnabled");

  const stopEnter = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" && (e.target as HTMLElement).tagName === "INPUT") e.preventDefault();
  };

  const passwordFields = (
    <>
      <div>
        <FieldLabel htmlFor="s-portal-pw">
          {t(account ? "suppliers.portalNewPassword" : "suppliers.portalPassword")}
        </FieldLabel>
        <Input
          id="s-portal-pw"
          type="password"
          autoComplete="new-password"
          minLength={8}
          maxLength={200}
          value={pw.password}
          onChange={(e) => setPw({ ...pw, password: e.target.value })}
          aria-describedby="s-portal-pw-hint"
        />
        {/* Lời dặn dài đặt DƯỚI ô: để cạnh nhãn thì nhãn bị bóp thành ba dòng và hai ô mật khẩu lệch nhau (18/09/2026). */}
        <p id="s-portal-pw-hint" className="mt-1 text-xs text-ink-faint">{t("suppliers.portalPasswordHint")}</p>
      </div>
      <div>
        <FieldLabel htmlFor="s-portal-pw2">{t("suppliers.portalPasswordConfirm")}</FieldLabel>
        <Input
          id="s-portal-pw2"
          type="password"
          autoComplete="new-password"
          maxLength={200}
          value={pw.confirm}
          onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
        />
        {mismatch ? <p className="mt-1 text-xs text-danger">{t("users.passwordMismatch")}</p> : null}
      </div>
    </>
  );

  const statusBadge = account ? (
    <Badge tone={account.status === "active" ? "ok" : "danger"}>
      {t(account.status === "active" ? "suppliers.portalStatusActive" : "suppliers.portalStatusDisabled")}
    </Badge>
  ) : (
    <Badge tone="neutral" outline>{t("suppliers.portalNoAccount")}</Badge>
  );

  const body = (
    <>
      {portal.isLoading ? (
        <p className="text-sm text-muted">{t("common.loading")}</p>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <FieldLabel htmlFor="s-portal">{t("suppliers.portalLoginId")}</FieldLabel>
            <div className="flex flex-wrap items-center gap-2">
              <Input
                id="s-portal"
                aria-describedby="s-portal-hint"
                className="max-w-xs font-mono"
                maxLength={40}
                value={loginId}
                onChange={(e) => setLoginIdDraft(e.target.value)}
              />
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={busy}
                onClick={() => setLoginIdDraft(generatePortalLoginId((n) => crypto.getRandomValues(new Uint8Array(n))))}
              >
                {t("suppliers.portalLoginGenerate")}
              </Button>
              {account && loginIdChanged ? (
                <Button type="button" size="sm" disabled={busy || !loginId.trim()} onClick={onSaveLoginId}>
                  {t("suppliers.portalSaveLoginId")}
                </Button>
              ) : null}
            </div>
            <p id="s-portal-hint" className="mt-1 text-xs text-ink-faint">{t("suppliers.portalLoginHint")}</p>
          </div>

          {account ? (
            <>
              <div className="text-sm sm:col-span-2">
                <p>{t(account.mustChangePassword ? "suppliers.portalMustChange" : "suppliers.portalPasswordChanged")}</p>
                <p className="text-muted">
                  {account.lastLoginAt
                    ? t("suppliers.portalLastLogin", { when: new Date(account.lastLoginAt).toLocaleString(i18n.language) })
                    : t("suppliers.portalNeverLoggedIn")}
                </p>
              </div>
              {passwordFields}
              <div className="flex flex-wrap gap-2 sm:col-span-2">
                <Button type="button" size="sm" variant="secondary" disabled={busy || !pwOk} onClick={onReset}>
                  {t("suppliers.portalResetPassword")}
                </Button>
                {account.status === "active" ? (
                  <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={() => onStatus("disabled")}>
                    {t("suppliers.portalDisable")}
                  </Button>
                ) : (
                  <Button type="button" size="sm" disabled={busy} onClick={() => onStatus("active")}>
                    {t("suppliers.portalEnable")}
                  </Button>
                )}
              </div>
            </>
          ) : (
            <>
              {passwordFields}
              <div className="sm:col-span-2">
                <Button type="button" size="sm" disabled={busy || !pwOk || !loginId.trim()} onClick={onOpen}>
                  {t("suppliers.portalOpen")}
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {error ? <p role="alert" className="rounded-sm bg-danger-tint px-3 py-2 text-sm text-danger">{error}</p> : null}
      {done ? <p role="status" className="text-sm text-ink-soft">{t(done)}</p> : null}
    </>
  );

  // Phím Enter bị chặn ở gốc khối trong cả hai dạng (xem chú thích đầu tệp).
  if (asGroup) {
    return (
      <div onKeyDown={stopEnter}>
        <FormGroup id="supplier-group-portal" title={t("suppliers.portalSection")} hint={t("suppliers.portalIntro")} actions={statusBadge}>
          {body}
        </FormGroup>
      </div>
    );
  }
  return (
    <Panel className="space-y-3 p-5" onKeyDown={stopEnter}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold text-ink">{t("suppliers.portalSection")}</h2>
        {statusBadge}
      </div>
      <p className="text-xs text-muted">{t("suppliers.portalIntro")}</p>
      {body}
    </Panel>
  );
}
